'use client';

import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import Image from 'next/image';

type SupportedLang = 'sl' | 'en';

export default function CebinovProjectPage() {
  const { currentLang } = useLanguage();
  const lang: SupportedLang = currentLang === 'en' ? 'en' : 'sl';

  const content = {
    sl: {
      pageLabel: 'Projekt',
      pageTitle: 'Inovativne rešitve pri čebelarjenju – ČEBINOV',
      metaTitle: 'Naziv projekta',
      metaLead: 'Inovativne rešitve pri čebelarjenju – ČEBINOV',
      holderLabel: 'Nosilec projekta',
      holderValue: 'Čebelarstvo Pliberšek, Andrej Pliberšek s.p.',
      partnersLabel: 'Partnerji v projektu',
      partnersValue: 'Občina Ruše, Čebelarska družina Ruše',
      totalValueLabel: 'Vrednost celotnega projekta',
      totalValueValue: '94.683,03 EUR',
      coFundingLabel: 'Sofinanciranje iz sredstev LEADER – po odločbi',
      coFundingValue: '63.026,68 EUR (EKSRP sklad)',
      durationLabel: 'Trajanje projekta',
      durationValue: 'oktober 2025 do oktober 2026',
      operationTitle: 'Opis operacije',
      operationParagraphs: [
        'Projekt »Inovativne rešitve pri čebelarjenju – ČEBINOV« naslavlja sodobne izzive podeželskega prostora z uvajanjem novih, trajnostno naravnanih dejavnosti v čebelarstvu, ki temeljijo na razvoju storitev in produktov z višjo dodano vrednostjo.',
        'Osrednji namen je oblikovanje inovativnih pristopov, ki prispevajo k razvoju zelene ekonomije, trajnostnega turizma in spodbudnemu okolju za ustvarjanje novih zaposlitvenih priložnosti na območju LAS Kozjak-Pohorje.',
        'Projekt zajema vzpostavitev podpornega okolja za inovativen pristop razvoja čebelarstva (nakup nove čebelarske mehanizacije), širitev specifičnih znanj (dogodki in delavnice znanja, razvoj novega podjetja), razvoj novega turističnega produkta (apiterapija) ter medgeneracijsko povezovanje in vključevanje ranljivih skupin.',
        'Razvoj novega turističnega produkta temelji na vključevanju naravnih danosti, lokalnih znanj in čebelarskih praks, še posebej z uvedbo nove storitve apiterapije.',
        'S povezovanjem čebelarjev, lokalnih skupnosti, izobraževalnih in turističnih akterjev operacija ustvarja temelje za dolgoročno trajnostno rast podeželja ter prispeva h konkurenčnosti, inovativnosti in ohranjanju identitete območja.',
      ],
      activitiesTitle: 'Aktivnosti',
      activities: [
        'Nakup inovativne čebelarske mehanizacije',
        'Ureditev prostora za opravljanje dejavnosti',
        'Izvedba 6 usposabljanj za čebelarje iz območja LAS',
        'Izdelava promocijskega materiala – dveh brošur za dejavnost in turistični produkt',
        'Izvedba delavnic za mlajšo in starejšo populacijo LAS ter poletnih čebelarskih šol za otroke',
        'Izdelava knjižice receptov',
        'Izvedba zaključne prireditve',
      ],
      goalsTitle: 'Cilji in rezultati projekta',
      goalsIntro:
        'Ključni cilj projekta je izboljšanje pogojev za delo čebelarjev, promocija čebelarskih produktov ter oblikovanje novega turističnega doživetja – apiterapije. Projekt prispeva k razvoju novega podjetja in novega produkta/dejavnosti na podeželju, hkrati pa spodbuja socialno vključenost, zdrav življenjski slog ter varovanje narave.',
      goalsSubtitle: 'Specifični cilji projekta',
      goals: [
        'Postavitev temeljev za inovativni razvoj in dolgoročno ohranjanje dediščine tradicionalnega čebelarstva',
        'Razvijanje in uveljavljanje inovativnih programov z visoko dodano vrednostjo za socialni razvoj in oblikovanje identitete območja',
        'Izboljšanje vključevanja ranljivih skupin v družbeno in socialno dejavnost na podeželju',
      ],
      interventionTitle: 'Intervencija',
      interventionValue: 'IRP26 – LEADER',
      interventionLinkLabel: 'Povezava do opisa intervencije',
      interventionLinkText: 'IRP26 LEADER | GOV.SI',
      interventionLinkHref:
        'https://www.gov.si/zbirke/projekti-in-programi/skupna-kmetijska-politika/strateski-nacrt-skupne-kmetijske-politike-20232027/intervencije-strateskega-nacrta-skupne-kmetijske-politike-2023-2027/irp26-leader/',
      capTitle: 'Skupna kmetijska politika',
      capLinkText: 'Skupna kmetijska politika - SKP',
      capLinkHref: 'https://skp.si/',
    },
    en: {
      pageLabel: 'Project',
      pageTitle: 'Innovative Solutions in Beekeeping – CEBINOV',
      metaTitle: 'Project Name',
      metaLead: 'Innovative Solutions in Beekeeping – CEBINOV',
      holderLabel: 'Project Holder',
      holderValue: 'Plibersek Beekeeping, Andrej Plibersek s.p.',
      partnersLabel: 'Project Partners',
      partnersValue: 'Municipality of Ruse, Beekeeping Association Ruse',
      totalValueLabel: 'Total Project Value',
      totalValueValue: 'EUR 94,683.03',
      coFundingLabel: 'Co-financing from LEADER funds – by decision',
      coFundingValue: 'EUR 63,026.68 (EAFRD fund)',
      durationLabel: 'Project Duration',
      durationValue: 'October 2025 to October 2026',
      operationTitle: 'Operation Description',
      operationParagraphs: [
        'The project “Innovative Solutions in Beekeeping – CEBINOV” addresses modern rural challenges by introducing new, sustainability-oriented beekeeping activities based on services and products with higher added value.',
        'Its central purpose is to develop innovative approaches that support the green economy, sustainable tourism, and a supportive environment for creating new employment opportunities in the LAS Kozjak-Pohorje area.',
        'The project includes creating a support environment for innovative beekeeping development (purchase of new beekeeping machinery), expanding specific knowledge (events and knowledge workshops, development of a new company), developing a new tourism product (apitherapy), and strengthening intergenerational cooperation and inclusion of vulnerable groups.',
        'The new tourism product is built on local natural assets, local knowledge, and beekeeping practices, especially through the introduction of a new apitherapy service.',
        'By connecting beekeepers, local communities, educational actors, and tourism stakeholders, the operation creates foundations for long-term sustainable rural growth and contributes to competitiveness, innovation, and preservation of local identity.',
      ],
      activitiesTitle: 'Activities',
      activities: [
        'Purchase of innovative beekeeping machinery',
        'Preparation of premises for carrying out activities',
        'Implementation of 6 trainings for beekeepers from the LAS area',
        'Preparation of promotional materials – two brochures for activities and the tourism product',
        'Workshops for younger and older LAS populations and summer beekeeping schools for children',
        'Preparation of a recipe booklet',
        'Implementation of a final public event',
      ],
      goalsTitle: 'Project Goals and Results',
      goalsIntro:
        'The key project goal is to improve working conditions for beekeepers, promote beekeeping products, and develop a new tourism experience – apitherapy. The project contributes to the development of a new rural company and a new product/activity while promoting social inclusion, a healthy lifestyle, and nature protection.',
      goalsSubtitle: 'Specific Project Goals',
      goals: [
        'Establish foundations for innovative development and long-term preservation of traditional beekeeping heritage',
        'Develop and implement innovative high value-added programs for social development and local identity building',
        'Improve inclusion of vulnerable groups in social and community activities in rural areas',
      ],
      interventionTitle: 'Intervention',
      interventionValue: 'IRP26 – LEADER',
      interventionLinkLabel: 'Link to intervention description',
      interventionLinkText: 'IRP26 LEADER | GOV.SI',
      interventionLinkHref:
        'https://www.gov.si/zbirke/projekti-in-programi/skupna-kmetijska-politika/strateski-nacrt-skupne-kmetijske-politike-20232027/intervencije-strateskega-nacrta-skupne-kmetijske-politike-2023-2027/irp26-leader/',
      capTitle: 'Common Agricultural Policy',
      capLinkText: 'Common Agricultural Policy - CAP',
      capLinkHref: 'https://skp.si/',
    },
  }[lang];

  return (
    <>
      <Header currentLang={currentLang} />
      <main className="pt-40 pb-32 px-6 md:px-10 max-w-5xl mx-auto">
        <Image src="/project.webp" width={100} height={100} alt="logo" />
        <div className="mb-14 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.4em] text-muted-foreground mb-4">
            {content.pageLabel}
          </p>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tighter">{content.pageTitle}</h1>
        </div>

        <section className="bg-card border border-border card-rounded p-6 md:p-8 mb-10 space-y-4">
          <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            {content.metaTitle}
          </p>
          <p className="text-lg font-semibold">{content.metaLead}</p>
          <div className="grid sm:grid-cols-2 gap-4 pt-2">
            <p>
              <span className="font-semibold">{content.holderLabel}: </span>
              {content.holderValue}
            </p>
            <p>
              <span className="font-semibold">{content.partnersLabel}: </span>
              {content.partnersValue}
            </p>
            <p>
              <span className="font-semibold">{content.totalValueLabel}: </span>
              {content.totalValueValue}
            </p>
            <p>
              <span className="font-semibold">{content.coFundingLabel}: </span>
              {content.coFundingValue}
            </p>
            <p className="sm:col-span-2">
              <span className="font-semibold">{content.durationLabel}: </span>
              {content.durationValue}
            </p>
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-5">
            {content.operationTitle}
          </h2>
          <div className="space-y-4 text-muted-foreground leading-relaxed">
            {content.operationParagraphs.map((paragraph, index) => (
              <p key={`operation_${index}`}>{paragraph}</p>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-5">
            {content.activitiesTitle}
          </h2>
          <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
            {content.activities.map((activity, index) => (
              <li key={`activity_${index}`}>{activity}</li>
            ))}
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-5">
            {content.goalsTitle}
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-5">{content.goalsIntro}</p>
          <p className="font-semibold mb-3">{content.goalsSubtitle}</p>
          <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
            {content.goals.map((goal, index) => (
              <li key={`goal_${index}`}>{goal}</li>
            ))}
          </ul>
        </section>

        <section className="bg-accent/10 card-rounded p-6 md:p-8 space-y-4">
          <p>
            <span className="font-semibold">{content.interventionTitle}: </span>
            {content.interventionValue}
          </p>
          <p>
            <span className="font-semibold">{content.interventionLinkLabel}: </span>
            <a
              href={content.interventionLinkHref}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {content.interventionLinkText}
            </a>
          </p>
          <p className="font-semibold">{content.capTitle}</p>
          <a
            href={content.capLinkHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            {content.capLinkText}
          </a>
        </section>
      </main>
      <Footer currentLang={currentLang} />
    </>
  );
}
